'use client';
import {
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectItemText,
  SelectValueText,
} from '@/components/ui/select';
import {
  CHAIN_IDS,
  CHAIN_INFO,
  checkChainId,
  ETHEREUM_CHAIN_ID,
  OMNI_CHAIN_ID,
} from '@/lib/config/chain';
import { Box, createListCollection, HStack, Image } from '@chakra-ui/react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useLocale } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { Tag } from '@/components/ui/tag';

const chainOptions = createListCollection<{
  label: string;
  value: string;
  icon: string;
}>({
  items: Object.values(CHAIN_INFO).map((value) => ({
    label: value.name,
    value: value.id,
    icon: value.icon,
  })),
});

export const MultiChainSelector = (
  props: {
    value?: string;
    onChange?: (value: string) => void;
    selectType?: 'DROPDOWN' | 'TAB';
    hideEth?: boolean;
    hideOmni?: boolean;
  } = {},
) => {
  const {
    value = '',
    onChange,
    selectType = 'DROPDOWN',
    hideEth = false,
    hideOmni = true,
  } = props;
  const locale = useLocale();
  const chainId = value;
  if (selectType === 'TAB') {
    return (
      <>
        <HStack display={'inline-flex'}>
          {chainOptions.items
            .filter((item) => {
              if (hideEth && item.value === ETHEREUM_CHAIN_ID) {
                return false;
              }
              if (hideOmni && item.value === OMNI_CHAIN_ID) {
                return false;
              }
              return true;
            })
            .map((item, item_index) => {
              return (
                <>
                  <Tag
                    key={item_index}
                    py={2}
                    pr={3}
                    pl={1}
                    bg="transparent"
                    color={item.value === chainId ? 'text.t1' : 'text.t3'}
                    border="1px solid"
                    borderColor={item.value === chainId ? 'text.t1' : 'text.t4'}
                    rounded="full"
                    justifyContent={'center'}
                    flex={'none'}
                    cursor={'pointer'}
                    onClick={(e) => {
                      e.stopPropagation();
                      const chainId = item.value;
                      onChange?.(chainId);
                    }}
                  >
                    <HStack gap="6px">
                      <Box>
                        <Image
                          src={item.icon}
                          alt={item.label}
                          w={5}
                          h={5}
                          rounded="full"
                          border="1px solid"
                          borderColor={'text.t4'}
                        />
                      </Box>
                      <Box fontWeight={500}>{item.label}</Box>
                    </HStack>
                  </Tag>
                </>
              );
            })}
        </HStack>
      </>
    );
  }
  return (
    <SelectRoot
      w="120px"
      collection={chainOptions}
      value={[chainId]}
      bg="text.t4"
      rounded="full"
      onValueChange={(e) => {
        const chainId = e.value[0];
        onChange?.(chainId);
      }}
    >
      <SelectTrigger rounded="full" cursor="pointer">
        <HStack gap="6px" pl={2}>
          <Image
            src={
              chainOptions.items.filter((item) => item.value === chainId)[0]
                .icon
            }
            alt={
              chainOptions.items.filter((item) => item.value === chainId)[0]
                .label
            }
            w={5}
            h={5}
            rounded="full"
          />
          <SelectValueText
            w="full"
            maxW="full"
            color="text.t1"
            fontWeight={500}
          />
        </HStack>
      </SelectTrigger>
      <SelectContent p={2} gap={1}>
        {chainOptions.items
          .filter((item) => {
            if (hideEth && item.value === ETHEREUM_CHAIN_ID) {
              return false;
            }
            if (hideOmni && item.value === OMNI_CHAIN_ID) {
              return false;
            }
            return true;
          })
          .map((item) => (
            <SelectItem
              key={item.value}
              item={item}
              py={2}
              px={3}
              gap={'6px'}
              cursor="pointer"
            >
              <Image
                src={item.icon}
                alt={item.label}
                w={5}
                h={5}
                rounded="full"
              />
              <SelectItemText color="text.t1">{item.label}</SelectItemText>
            </SelectItem>
          ))}
      </SelectContent>
    </SelectRoot>
  );
};

export function MultiChainSelectorWithSearchParams(
  props: {
    selectType?: 'DROPDOWN' | 'TAB';
    hideEth?: boolean;
    hideOmni?: boolean;
  } = {},
) {
  const { selectType, hideEth = false, hideOmni = true } = props;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const defaultChainId = searchParams.get('chainId') || checkChainId();
  const [chainId, setChainId] = useState<string>(defaultChainId);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const chainIdFromParams = searchParams.get('chainId') || checkChainId();
    setChainId(chainIdFromParams);
  }, [pathname, searchParams]);

  return (
    <MultiChainSelector
      value={chainId}
      onChange={(chainId) => {
        router.replace(`${pathname}?chainId=${chainId}`);
      }}
      selectType={selectType}
      hideEth={hideEth}
      hideOmni={hideOmni}
    />
  );
}
